import React, { useState, useEffect, useRef } from "react";
import { CloudUpload, AlertCircle, X } from "lucide-react";

const FormMultipleInputFile = (
    { 
        field, 
        handleChange, 
        error = null, 
        imagePreview 
    }) => {
    const inputRef = useRef(null);

    const [preview, setPreview] = useState([]);
    const [fileNames, setFileNames] = useState([]);
    const [ratioError, setRatioError] = useState("");
    const [invalidImages, setInvalidImages] = useState([]);

    const parseRatio = (ratio) => {
        if (!ratio) return null;
        const [w, h] = ratio.split("/").map(Number);
        return w && h ? w / h : null;
    };

    const showPreview = (event) => {
        const files = Array.from(event.target.files || []);
        setRatioError("");
        setInvalidImages([]);

        if (!files.length) {
            setPreview([]);
            setFileNames([]);
            handleChange(event);
            return;
        }

        const expectedRatio = parseRatio(field.accept_ratio);
        const tolerance = 0.05;

        const validPreviews = [];
        const validNames = [];
        const invalidPreviews = [];
        const validFiles = [];
        let processedCount = 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const reader = new FileReader();

            reader.onload = (e) => {
                const imageData = e.target.result;
                const img = new Image();

                img.onload = () => {
                    processedCount++;

                    if (expectedRatio) {
                        const actualRatio = img.width / img.height;
                        const ratioDifference = Math.abs(actualRatio - expectedRatio);

                        if (ratioDifference > tolerance) {
                            // Store invalid image data
                            invalidPreviews.push({
                                preview: imageData,
                                name: file.name,
                                expected: field.accept_ratio,
                                actual: `${img.width}x${img.height}`,
                                actualRatio: actualRatio.toFixed(2),
                                file: file
                            });
                            finalizeIfDone();
                            return;
                        }
                    }

                    // Store valid image data
                    validPreviews.push(imageData);
                    validNames.push(file.name);
                    validFiles.push(file);
                    finalizeIfDone();
                };

                img.onerror = () => {
                    processedCount++;
                    // Handle corrupted/invalid image files
                    invalidPreviews.push({
                        preview: imageData,
                        name: file.name,
                        expected: field.accept_ratio || 'Valid image',
                        actual: 'Failed to load image',
                        actualRatio: null,
                        file: file
                    });
                    finalizeIfDone();
                };

                img.src = imageData;
            };

            reader.onerror = () => {
                processedCount++;
                invalidPreviews.push({
                    preview: null,
                    name: file.name,
                    expected: field.accept_ratio || 'Valid file',
                    actual: 'Failed to read file',
                    actualRatio: null,
                    file: file
                });
                finalizeIfDone();
            };

            reader.readAsDataURL(file);
        }

        const finalizeIfDone = () => {
            if (processedCount === files.length) {
                // Set valid previews
                setPreview(validPreviews);
                setFileNames(validNames);

                // Store invalid images for display
                setInvalidImages(invalidPreviews);

                // Set error message if there are invalid images
                if (invalidPreviews.length > 0) {
                    setRatioError(
                        `${invalidPreviews.length} image(s) rejected. Required ratio: ${field.accept_ratio}`
                    );
                }

                // Update input with only valid files
                const dt = new DataTransfer();
                validFiles.forEach(file => dt.items.add(file));
                inputRef.current.files = dt.files;
                handleChange({ target: inputRef.current });
            }
        };
    };

    const remove = (index, e) => {
        e.stopPropagation();
        e.preventDefault();

        setPreview(prev => prev.filter((_, i) => i !== index));
        setFileNames(prev => prev.filter((_, i) => i !== index));

        const dt = new DataTransfer();
        const files = Array.from(inputRef.current.files);

        files.forEach((file, i) => {
            if (i !== index) {
                dt.items.add(file);
            }
        });

        inputRef.current.files = dt.files;
        handleChange({ target: inputRef.current });
    };

    const removeInvalidImage = (index) => {
        setInvalidImages(prev => prev.filter((_, i) => i !== index));
        if (invalidImages.length === 1) {
            setRatioError("");
        }
    };

    useEffect(() => {
        if (imagePreview) {
            setPreview(Array.isArray(imagePreview) ? imagePreview : [imagePreview]);
        }
    }, [imagePreview]);

    return (
        <div  className={field.fieldSize || "col-12"}>
            <div className="upload-container multiple mt-3">
                <label
                    htmlFor={field.name}
                    className={`upload-header ${preview.length ? "has-image" : ""}`}
                    data-count={preview.length ? `${preview.length} files` : null}
                >
                    {!preview.length ? (
                        <div className="upload-file w-100">
                            <CloudUpload size={70} strokeWidth={1} className="upload-icon" />
                            <p>{field.label} Upload</p>
                            {field.accept_ratio && (
                                <small>Required ratio: {field.accept_ratio}</small>
                            )}
                        </div>
                    ) : (
                        <div className="preview-grid d-flex gap-2 flex-wrap">
                            {preview.map((src, index) => (
                                <div
                                    key={index}
                                    className="d-flex flex-column align-items-center position-relative"
                                >
                                    <img
                                        src={src}
                                        alt="Preview"
                                        className="upload-preview avatar xxl"
                                    />
                                    <span
                                        className="text-danger remove-icon cursor-pointer rounded-circle bg-dark position-absolute top-0 end-0"
                                        onClick={(e) => remove(index, e)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M18 6l-12 12" />
                                            <path d="M6 6l12 12" />
                                        </svg>
                                    </span>
                                    <span
                                        className="file-name-label small text-truncate"
                                        style={{ maxWidth: "100px" }}
                                        title={fileNames[index]}
                                    >
                                        {fileNames[index]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </label>

                <input
                    ref={inputRef}
                    id={field.name}
                    type="file"
                    name={field.name}
                    accept={field.accept || "image/*"}
                    onChange={showPreview}
                    className="d-none"
                    multiple={field.multiple ?? false}
                />

                {ratioError && (
                    <div className="alert alert-warning mt-2 d-flex align-items-center gap-2 py-2">
                        <AlertCircle size={18} />
                        <span className="small">{ratioError}</span>
                    </div>
                )}

                {/* Invalid Images Section */}
                {invalidImages.length > 0 && (
                    <div className="invalid-images-section">
                        <div className="invalid-images-header">
                            <AlertCircle size={20} />
                            <h6>Invalid Images ({invalidImages.length})</h6>
                        </div>

                        <div className="invalid-images-list">
                            {invalidImages.map((item, index) => (
                                <div key={index} className="invalid-image-item">
                                    <div className="invalid-image-content">
                                        {/* Preview thumbnail */}
                                        <div className="invalid-image-preview">
                                            {item.preview ? (
                                                <img
                                                    src={item.preview}
                                                    alt={item.name}
                                                />
                                            ) : (
                                                <X size={32} />
                                            )}
                                        </div>

                                        {/* Error details */}
                                        <div className="invalid-image-details">
                                            <span className="invalid-image-name" title={item.name}>
                                                {item.name}
                                            </span>
                                            <div className="invalid-image-expected">
                                                Expected: {item.expected}
                                            </div>
                                            <div className="invalid-image-actual">
                                                Actual: {item.actual} {item.actualRatio && `(${item.actualRatio}:1)`}
                                            </div>
                                        </div>

                                        {/* Remove button */}
                                        <button
                                            type="button"
                                            onClick={() => removeInvalidImage(index)}
                                            className="invalid-image-remove"
                                            title="Remove from list"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="invalid-images-tip">
                            Tip: Please upload images with the correct aspect ratio ({field.accept_ratio})
                        </div>
                    </div>
                )}

                {error && <span className="text-danger mt-2 d-block">{error}</span>}
            </div>
        </div>
    );
};

export default FormMultipleInputFile;